/**
 * Client-side Logger
 *
 * Captures console errors in the browser and sends them to the backend
 */

interface ClientLogEntry {
  timestamp: string;
  level: 'error' | 'warn' | 'info' | 'log';
  message: string;
  stack?: string;
  url: string;
  userAgent: string;
}

/**
 * Send log to backend
 */
async function sendLogToServer(entry: ClientLogEntry): Promise<void> {
  try {
    await fetch('/api/logs/client', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(entry),
    });
  } catch (err) {
    // Silently fail - don't spam console with logging errors
  }
}

/**
 * Initialize client-side error logging (override console methods)
 */
export function initClientLogging(): void {
  // Only run on client
  if (typeof window === 'undefined') return;

  // Store original console methods
  const originalError = console.error;
  const originalWarn = console.warn;

  // Override console.error
  console.error = (...args: any[]) => {
    const message = args.map(arg =>
      typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
    ).join(' ');

    const error = args.find(arg => arg instanceof Error);

    sendLogToServer({
      timestamp: new Date().toISOString(),
      level: 'error',
      message,
      stack: error?.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
    });

    // Call original
    originalError.apply(console, args);
  };

  // Override console.warn
  console.warn = (...args: any[]) => {
    const message = args.map(arg =>
      typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
    ).join(' ');

    sendLogToServer({
      timestamp: new Date().toISOString(),
      level: 'warn',
      message,
      url: window.location.href,
      userAgent: navigator.userAgent,
    });

    // Call original
    originalWarn.apply(console, args);
  };

  // Handle uncaught errors
  window.addEventListener('error', (event) => {
    sendLogToServer({
      timestamp: new Date().toISOString(),
      level: 'error',
      message: event.message,
      stack: event.error?.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
    });
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    sendLogToServer({
      timestamp: new Date().toISOString(),
      level: 'error',
      message: `Unhandled promise rejection: ${event.reason}`,
      stack: event.reason?.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
    });
  });
}
